import { getDashboardSummary } from "app/services/dashboardService";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, ButtonGroup, Col, Form, Row, Spinner } from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

// import L from "leaflet";
// import {
//   MapContainer,
//   Marker,
//   Polyline,
//   Popup,
//   TileLayer,
// } from "react-leaflet";

// import { getDashboardSummary } from "app/services/dashboardService";
// import markerIcon from "leaflet/dist/images/marker-icon.png";
// import markerShadow from "leaflet/dist/images/marker-shadow.png";
// import "leaflet/dist/leaflet.css";

const TIME_FILTERS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
  { key: "previousYear", label: "Previous Year" },
  { key: "particularDate", label: "Particular Date" },
  { key: "dateRange", label: "Date Range" },
];

const CHART_TYPES = [
  { key: "line", label: "Line" },
  { key: "bar", label: "Bar" },
  { key: "pie", label: "Pie" },
];

const JOURNEY_TYPES = [
  { key: "all", label: "All" },
  { key: "domestic", label: "Domestic" },
  { key: "international", label: "International" },
];

const AIRLINE_COLORS = [
  "#D4A574",
  "#2C3E50",
  "#34495E",
  "#95A5A6",
  "#6366f1",
  "#10b981",
  "#ef4444",
  "#f59e0b",
];
/* ------------------------------------------------------------------ */
/* Small presentational components                                     */
/* ------------------------------------------------------------------ */

const cardStyle = {
  backgroundColor: "var(--color-background-primary)",
  padding: "1.5rem",
  borderRadius: "var(--border-radius-lg)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
};

const sectionTitleStyle = {
  fontSize: "16px",
  fontWeight: 600,
  margin: 0,
  color: "var(--color-text-primary)",
};

function OverviewCard({
  title,
  totalLabel,
  totalValue,
  split,
  formatter = (v) => v,
}) {
  const [mode, setMode] = useState("total"); // 'total' | 'split'
  const toggle = () => setMode((m) => (m === "total" ? "split" : "total"));

  return (
    <div style={cardStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <h2 style={sectionTitleStyle}>{title}</h2>
        <Button size="sm" variant="outline-secondary" onClick={toggle}>
          {mode === "total" ? "Split" : "Total"}
        </Button>
      </div>

      {mode === "total" ? (
        <div>
          <div
            style={{
              fontSize: 12,
              color: "var(--color-text-secondary)",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {totalLabel}
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "var(--color-text-primary)",
            }}
          >
            {totalValue}
          </div>
        </div>
      ) : (
        <Row>
          <Col xs={6}>
            <div
              style={{
                fontSize: 12,
                color: "var(--color-text-secondary)",
                textTransform: "uppercase",
              }}
            >
              Domestic
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#10b981" }}>
              {split.domestic}
            </div>
          </Col>
          <Col xs={6}>
            <div
              style={{
                fontSize: 12,
                color: "var(--color-text-secondary)",
                textTransform: "uppercase",
              }}
            >
              International
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#6366f1" }}>
              {split.international}
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
}

function FiltersBar({ filters, setFilters, airlines }) {
  const update = (patch) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <div style={{ ...cardStyle, marginBottom: "1.5rem" }}>
      <Row className="g-3 align-items-end">
        {/* Time filter */}
        <Col md={2}>
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Time Filter
          </Form.Label>
          <Form.Select
            size="sm"
            value={filters.timeFilter}
            onChange={(e) => update({ timeFilter: e.target.value })}
          >
            {TIME_FILTERS.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        {/* Particular date */}
        {filters.timeFilter === "particularDate" && (
          <Col md={2}>
            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
              Date
            </Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={filters.particularDate || ""}
              onChange={(e) => update({ particularDate: e.target.value })}
            />
          </Col>
        )}

        {/* Date range */}
        {filters.timeFilter === "dateRange" && (
          <>
            <Col md={2}>
              <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                From
              </Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.fromDate || ""}
                onChange={(e) => update({ fromDate: e.target.value })}
              />
            </Col>
            <Col md={2}>
              <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                To
              </Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.toDate || ""}
                onChange={(e) => update({ toDate: e.target.value })}
              />
            </Col>
          </>
        )}

        {/* Chart type */}
        <Col md="auto">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Chart Type
          </Form.Label>
          <div>
            <ButtonGroup size="sm">
              {CHART_TYPES.map((c) => (
                <Button
                  key={c.key}
                  variant={
                    filters.chartType === c.key ? "primary" : "outline-primary"
                  }
                  onClick={() => update({ chartType: c.key })}
                >
                  {c.label}
                </Button>
              ))}
            </ButtonGroup>
          </div>
        </Col>

        {/* Journey type */}
        <Col md="auto">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Journey Type
          </Form.Label>
          <div>
            <ButtonGroup size="sm">
              {JOURNEY_TYPES.map((j) => (
                <Button
                  key={j.key}
                  variant={
                    filters.journeyType === j.key
                      ? "primary"
                      : "outline-primary"
                  }
                  onClick={() => update({ journeyType: j.key })}
                >
                  {j.label}
                </Button>
              ))}
            </ButtonGroup>
          </div>
        </Col>

        {/* Airline */}
        <Col md={2}>
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Airline
          </Form.Label>
          <Form.Select
            size="sm"
            value={filters.airlineCode}
            onChange={(e) => update({ airlineCode: e.target.value })}
          >
            <option value="all">All</option>
            {airlines.map((a) => (
              <option key={a.code} value={a.code}>
                {a.airline}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Chart blocks                                                        */
/* ------------------------------------------------------------------ */

function TicketSalesChart({ data, chartType }) {
  const renderChart = () => {
    if (chartType === "bar") {
      return (
        <BarChart data={data}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--color-border-tertiary)"
          />
          <XAxis dataKey="period" stroke="var(--color-text-secondary)" />
          <YAxis stroke="var(--color-text-secondary)" />
          <Tooltip />
          <Legend />
          <Bar dataKey="sales" fill="#D4A574" name="Tickets" />
        </BarChart>
      );
    }
    if (chartType === "pie") {
      return (
        <PieChart>
          <Pie
            data={data}
            dataKey="sales"
            nameKey="period"
            cx="50%"
            cy="50%"
            outerRadius={100}
            label={(e) => `${e.period}: ${e.sales}`}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={AIRLINE_COLORS[i % AIRLINE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      );
    }
    return (
      <LineChart data={data}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="var(--color-border-tertiary)"
        />
        <XAxis dataKey="period" stroke="var(--color-text-secondary)" />
        <YAxis stroke="var(--color-text-secondary)" />
        <Tooltip />
        <Legend />
        <Line
          type="monotone"
          dataKey="sales"
          stroke="#D4A574"
          strokeWidth={2}
          dot={{ r: 4 }}
          name="Tickets"
        />
      </LineChart>
    );
  };

  return (
    <div style={cardStyle}>
      <h2 style={{ ...sectionTitleStyle, marginBottom: "1rem" }}>
        Ticket Sales
      </h2>
      <ResponsiveContainer width="100%" height={320}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
}

function AirlinePieCard({ distribution }) {
  const [mode, setMode] = useState("all");
  const data = distribution[mode] || [];

  return (
    <div style={cardStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <h2 style={sectionTitleStyle}>Bookings by Airline</h2>
        <ButtonGroup size="sm">
          {["all", "domestic", "international"].map((m) => (
            <Button
              key={m}
              variant={mode === m ? "primary" : "outline-primary"}
              onClick={() => setMode(m)}
            >
              {m[0].toUpperCase() + m.slice(1)}
            </Button>
          ))}
        </ButtonGroup>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={data}
            dataKey="bookings"
            nameKey="airline"
            cx="50%"
            cy="50%"
            outerRadius={100}
            label={(e) => `${e.airline} (${e.percentage}%)`}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={AIRLINE_COLORS[i % AIRLINE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(v) => `${v} bookings`} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Served Area map                                                     */
/* ------------------------------------------------------------------ */
/*
 * NOTE: We render the map via react-leaflet if available; otherwise
 * we fall back to a route list so the dashboard never breaks even
 * when leaflet isn't installed yet.
 *
 *   npm i leaflet react-leaflet
 *   // and include leaflet CSS in your index.html or App.js:
 *   //   import "leaflet/dist/leaflet.css";
 */
// function ServedAreaMap({ routes }) {
//   // ✅ Fix marker icon issue
//   useEffect(() => {
//     delete L.Icon.Default.prototype._getIconUrl;

//     L.Icon.Default.mergeOptions({
//       iconUrl: markerIcon,
//       shadowUrl: markerShadow,
//     });
//   }, []);

//   return (
//     <div style={cardStyle}>
//       <h2 style={{ ...sectionTitleStyle, marginBottom: "1rem" }}>
//         Served Area
//       </h2>

//       <div style={{ height: 420, borderRadius: 8, overflow: "hidden" }}>
//         <MapContainer
//           center={[20.5937, 78.9629]}
//           zoom={4}
//           style={{ height: "100%", width: "100%" }}
//         >
//           <TileLayer
//             attribution="&copy; OpenStreetMap contributors"
//             url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//           />

//           {routes.map((r, i) => (
//             <React.Fragment key={i}>
//               {/* FROM */}
//               <Marker position={[r.fromLat, r.fromLng]}>
//                 <Popup>
//                   <b>{r.from}</b> (From)
//                 </Popup>
//               </Marker>

//               {/* TO */}
//               <Marker position={[r.toLat, r.toLng]}>
//                 <Popup>
//                   <b>{r.to}</b> (To)
//                 </Popup>
//               </Marker>

//               {/* ROUTE LINE */}
//               <Polyline
//                 positions={[
//                   [r.fromLat, r.fromLng],
//                   [r.toLat, r.toLng],
//                 ]}
//                 pathOptions={{
//                   color: "#D4A574",
//                   weight: 3,
//                 }}
//               />
//             </React.Fragment>
//           ))}
//         </MapContainer>
//       </div>
//     </div>
//   );
// }

export default function FlightBookingDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // format: YYYY-MM-DD
  };

  const [filters, setFilters] = useState({
    timeFilter: "today",
    particularDate: getTodayDate(),
    fromDate: getTodayDate(),
    toDate: getTodayDate(),
    chartType: "line",
    journeyType: "all",
    airlineCode: "all",
  });

  const fetchData = useCallback(async () => {
    // Cancel any in-flight request when filters change rapidly.
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setLoading(true);
    setError(null);
    try {
      const resp = await getDashboardSummary(filters, ac.signal);
      debugger;
      setData(resp);
    } catch (e) {
      if (e.name === "CanceledError" || e.name === "AbortError") return;
      setError(
        e?.response?.data?.message || e.message || "Failed to load dashboard",
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  const airlines = useMemo(
    () => (data ? data.airlineDistribution.all : []),
    [data],
  );

  if (loading && !data) {
    return (
      <>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <Spinner animation="border" role="status" />
          <p style={{ color: "var(--color-text-secondary)", fontSize: 16 }}>
            Loading dashboard...
          </p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div
          style={{
            padding: "2rem",
            textAlign: "center",
            color: "var(--color-text-danger)",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: "1rem" }}>⚠️</div>
          <p style={{ fontWeight: 600 }}>Failed to load dashboard</p>
          <p style={{ fontSize: 14 }}>{error}</p>
          <Button variant="danger" onClick={fetchData}>
            Try Again
          </Button>
        </div>
      </>
    );
  }

  if (!data) return null;

  const {
    journeyOverview,
    bookingOverview,
    businessOverview,
    airlineDistribution,
    ticketSales,
    servedAreas,
  } = data;

  const currencyFmt = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: businessOverview.currency || "INR",
      maximumFractionDigits: 0,
    }).format(v);

  return (
    <>
      <div style={{ padding: "1.5rem", opacity: loading ? 0.6 : 1 }}>
        <FiltersBar
          filters={filters}
          setFilters={setFilters}
          airlines={airlines}
        />

        <Row className="g-4 mb-4">
          <Col lg={4} md={6}>
            <OverviewCard
              title="Journey Overview"
              totalLabel="Total Journeys"
              totalValue={journeyOverview.total}
              split={{
                domestic: journeyOverview.domestic,
                international: journeyOverview.international,
              }}
            />
          </Col>
          <Col lg={4} md={6}>
            <OverviewCard
              title="Booking Overview"
              totalLabel="Total Bookings"
              totalValue={bookingOverview.total}
              split={{
                domestic: bookingOverview.domestic,
                international: bookingOverview.international,
              }}
            />
          </Col>
          <Col lg={4} md={6}>
            <OverviewCard
              title="Business Overview"
              totalLabel="Total Business Volume"
              totalValue={businessOverview.total}
              split={{
                domestic: businessOverview.domestic,
                international: businessOverview.international,
              }}
              formatter={currencyFmt}
            />
          </Col>
        </Row>

        <Row className="g-4 mb-4">
          <Col lg={6}>
            <AirlinePieCard distribution={airlineDistribution} />
          </Col>
          <Col lg={6}>
            <TicketSalesChart
              data={ticketSales}
              chartType={filters.chartType}
            />
          </Col>
        </Row>

        <Row className="g-4">
          <Col lg={12}>{/* <ServedAreaMap routes={servedAreas} /> */}</Col>
        </Row>
      </div>
    </>
  );
}
